import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// src/features/workbooks/new.tsx

import { ExternalLink } from "@/components/ExternalLink";
import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import {
  CLIENT_GROUP_OPTIONS,
  CLIENT_GROUP_VALUES,
} from "@/features/Workspaces/clientGroups";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// Function to adjust AutocompleteAsyncField data for submission
const adjustDataForSubmission = (data: any) => {
  const adjustedData = { ...data };

  // Handle group_user_name AutocompleteAsyncField
  if (data.group_user_name && typeof data.group_user_name === "object") {
    // Extract the ID from the autocomplete object
    adjustedData.user_group_id = data.group_user_name.id || data.user_group_id;
  } else if (data.group_user_name == null) {
    adjustedData.user_group_id = null;
  }
  delete adjustedData.group_user_name;

  return adjustedData;
};

const FormSchema = z.object({
  client: z.string().nullable().optional(),
  workspace_group: z.enum(CLIENT_GROUP_VALUES).nullable().optional(),
  name: z.string().min(1, "Name is required"),
  menu: z.string().min(1, "Menu is required"),
  menu_quantilia: z.string().min(1, "Menu Quantilia is required"),
  sort_order: z.coerce.number().optional().nullable(),
  uuid: z.string().min(1, "UUID is required"),
  group_user_name: z
    .union([
      z.string(),
      z.object({
        id: z.string(),
        name: z.string(),
        text: z.string(),
      }),
    ])
    .nullable()
    .optional(),
  user_group_id: z.string().nullable().optional(),
});

export function WorkbooksEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "group_user_name") {
        return {
          text: fetchedData.group_user_name,
          id: fetchedData.user_group_id,
          name: fetchedData.group_user_name,
        };
      }
      return value;
    },
  });

  const name = form.watch("name");

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Workbooks {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Client & Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="uuid"
                    label={t(`${table_name}.columns.uuid`, {
                      ns: "tables",
                      defaultValue: "UUID",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 2: Menu & Menu Quantilia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="menu"
                    label={t(`${table_name}.columns.menu`, {
                      ns: "tables",
                      defaultValue: "Menu",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="menu_quantilia"
                    label={t(`${table_name}.columns.menu_quantilia`, {
                      ns: "tables",
                      defaultValue: "Menu Quantilia",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 3: Workspace Group & Client */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="workspace_group"
                    label={t(`${table_name}.columns.workspace_group`, {
                      ns: "tables",
                      defaultValue: "Workspace Group",
                    })}
                    options={CLIENT_GROUP_OPTIONS}
                    allow_empty_option
                    control={form.control}
                  />
                  <InputField
                    name="client"
                    label={t(`${table_name}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 4: Sort Order */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="sort_order"
                    label={t(`${table_name}.columns.sort_order`, {
                      ns: "tables",
                      defaultValue: "Sort Order",
                    })}
                    type="number"
                    control={form.control}
                    allowZero
                  />
                </div>
                {/* Row 5: User Group */}
                <AutocompleteAsyncField
                  name="group_user_name"
                  label={t(`${table_name}.columns.group_user_name`, {
                    ns: "tables",
                    defaultValue: "User Group",
                  })}
                  base_url="tableau-quantilia/workbooks"
                  column_name="group_user_name"
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, [""]) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
        {name && (
          <div className="absolute top-[3.20rem] right-12 2xl:right-10 lg:flex gap-2 flex-row hidden">
            <Button
              href={toWorkspacePath(
                `/admin/qtl - tableau/views?workbook_id_contains=${name}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Views
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
